// Resolves the LocalizedString fields of a RoomsConfig down to plain strings
// for one locale, in place.
//
// DELIBERATE DEVIATION from the phase plan, which said "all read sites route
// through localize(...)". That would thread a `locale` argument into eleven
// modules — variantPanel, floorplan, topbar, infoModal, ctaPanel, materialPanel,
// narrator, tourReport, dashboard, editorPanel, hotspotManager — and every one
// of them is a place a future field can be added and quietly forgotten. Here
// the walk is the single list of what is localisable, a missed field is a
// missed entry in ONE table, and every consumer keeps reading `string`.
//
// In place, not a copy, and that is the load-bearing part: RoomManager,
// VariantManager and HotspotManager all hold references to the very objects
// inside RoomsConfig. Rebuilding the config on a locale switch would leave
// every manager pointing at the previous language. Mutating the fields keeps
// object identity, so switching locale is "rewrite the strings, re-emit the
// current room" rather than "rebuild the world".
//
// The raw values are captured once, at collect time, because the resolved
// string overwrites them: without the snapshot, pt-BR → en → pt-BR would read
// the English string as if it were the original data.

import type { Locale } from '../i18n';
import { localize, type LocalizedString } from '../i18n/localize.ts';
import type { RoomsConfig } from './types';

/** One localisable field, with the original value it was collected from. */
interface Slot {
  obj: Record<string, unknown>;
  key: string;
  raw: LocalizedString;
}

/** Same, for `string[]` fields (room features, apartment amenities). */
interface ArraySlot {
  obj: Record<string, unknown>;
  key: string;
  raw: LocalizedString[];
}

export interface LocalizedConfigBinding {
  slots: Slot[];
  arraySlots: ArraySlot[];
}

/**
 * Field names that carry buyer-facing prose, per node type.
 *
 * Anything NOT listed here stays untouched, which is why ids, urls, variant
 * keys and every number pass through unharmed. Adding a localisable field to
 * types.ts means adding it here — that is the whole maintenance burden.
 */
const BRANDING_FIELDS = ['name', 'subtitle'] as const;
const CTA_FIELDS = ['defaultLabel', 'defaultMessage'] as const;
const APARTMENT_FIELDS = ['solarOrientation', 'neighborhood', 'cityState', 'deliveryDate', 'priceRangeBRL'] as const;
const FAQ_FIELDS = ['q', 'a'] as const;
const ROOM_FIELDS = ['name', 'subtitle', 'narration', 'solarOrientation'] as const;
const VARIANT_FIELDS = ['name'] as const;
// `label` is on every hotspot; the rest exist only on some types, and reading a
// key that is not there is simply skipped.
const HOTSPOT_FIELDS = ['label', 'description', 'ctaLabel', 'imageAlt'] as const;
const SWATCH_FIELDS = ['label'] as const;
const FEATURE_FIELDS = ['label'] as const;

/** Records every localisable field reachable from `config`. */
export function collectLocalizedSlots(config: RoomsConfig): LocalizedConfigBinding {
  const slots: Slot[] = [];
  const arraySlots: ArraySlot[] = [];

  const take = (node: unknown, fields: readonly string[]): void => {
    if (!node || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    for (const key of fields) {
      const raw = obj[key];
      if (isLocalized(raw)) slots.push({ obj, key, raw });
    }
  };

  const takeArray = (node: unknown, key: string): void => {
    if (!node || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    const raw = obj[key];
    if (!Array.isArray(raw)) return;
    if (!raw.every(isLocalized)) return;
    arraySlots.push({ obj, key, raw: [...raw] as LocalizedString[] });
  };

  take(config.branding, BRANDING_FIELDS);
  take(config.cta, CTA_FIELDS);
  take(config.apartment, APARTMENT_FIELDS);
  takeArray(config.apartment, 'amenities');
  for (const entry of config.faq ?? []) take(entry, FAQ_FIELDS);
  for (const feature of config.features ?? []) take(feature, FEATURE_FIELDS);

  for (const room of config.rooms ?? []) {
    take(room, ROOM_FIELDS);
    takeArray(room, 'features');
    for (const variant of room.variants ?? []) take(variant, VARIANT_FIELDS);
    for (const hotspot of room.hotspots ?? []) {
      take(hotspot, HOTSPOT_FIELDS);
      // Surface hotspots carry two swatch lists, each with its own label.
      const surface = hotspot as { swatches?: unknown[]; materials?: unknown[] };
      for (const swatch of surface.swatches ?? []) take(swatch, SWATCH_FIELDS);
      for (const finish of surface.materials ?? []) take(finish, SWATCH_FIELDS);
    }
  }

  return { slots, arraySlots };
}

/** Writes the `locale` rendering of every collected field back onto the config. */
export function applyLocale(binding: LocalizedConfigBinding, locale: Locale): void {
  for (const slot of binding.slots) {
    slot.obj[slot.key] = localize(slot.raw, locale);
  }
  for (const slot of binding.arraySlots) {
    slot.obj[slot.key] = slot.raw.map((entry) => localize(entry, locale));
  }
}

/** collect + apply, for the boot path. Keep the binding to switch locale later. */
export function localizeConfig(config: RoomsConfig, locale: Locale): LocalizedConfigBinding {
  const binding = collectLocalizedSlots(config);
  applyLocale(binding, locale);
  return binding;
}

/**
 * A plain string counts: it is a pt-BR value that needs no rewriting, but it
 * still has to be captured, because `en` must fall back to it rather than to
 * whatever a previous applyLocale() left behind.
 */
function isLocalized(value: unknown): value is LocalizedString {
  if (typeof value === 'string') return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return typeof (value as Record<string, unknown>)['pt-BR'] === 'string';
}
